import React, { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { INR, fmtDate, titleCase, StatusBadge } from "@/lib/format";
import { PageHeader, DataTable, Pager, useList } from "@/components/shared";
import { FormDialog } from "@/components/EntityForm";
import { Button } from "@/components/ui/button";
import { Wallet, Plus } from "lucide-react";

export default function Payments() {
  const { hasPerm } = useAuth();
  const [policies, setPolicies] = useState([]);
  const { items, total, loading, page, setPage, refetch } = useList("/payments", {});
  useEffect(() => { api.get("/policies", { params: { limit: 100 } }).then(({ data }) => setPolicies(data.items || [])).catch(() => {}); }, []);
  const create = async (v) => { await api.post("/payments", v); refetch(); };
  const fields = [
    { name: "policy_id", label: "Policy", type: "select", required: true, options: policies.map((p) => ({ value: p.id, label: `${p.policy_number} — ${p.customer_name || ""} (${INR(p.premium)})` })) },
    { name: "amount_received", label: "Amount Received (₹)", type: "number", required: true },
    { name: "payment_mode", label: "Payment Mode", type: "select", options: ["UPI", "Card", "NetBanking", "Cheque", "Cash"] },
    { name: "transaction_reference", label: "Transaction Reference", placeholder: "Required to mark as verified/paid" },
    { name: "payment_date", label: "Payment Date", type: "date" },
  ];
  const columns = [
    { key: "receipt_number", header: "Receipt", render: (r) => <span className="font-mono text-xs">{r.receipt_number}</span> },
    { key: "policy_number", header: "Policy", render: (r) => <span className="font-mono text-xs">{r.policy_number || "—"}</span> },
    { key: "policy_booked_amount", header: "Booked", tdClass: "text-right", thClass: "text-right", render: (r) => <span className="tabular">{INR(r.policy_booked_amount)}</span> },
    { key: "amount_received", header: "Received", tdClass: "text-right", thClass: "text-right", render: (r) => <span className="tabular font-semibold text-emerald-600">{INR(r.amount_received)}</span> },
    { key: "pending_amount", header: "Pending", tdClass: "text-right", thClass: "text-right", render: (r) => <span className="tabular text-amber-600">{INR(r.pending_amount)}</span> },
    { key: "payment_status", header: "Status", render: (r) => <StatusBadge value={r.payment_status} /> },
    { key: "payment_date", header: "Date", render: (r) => <span className="text-sm">{fmtDate(r.payment_date)}</span> },
  ];
  return (
    <div>
      <PageHeader title="Payments" subtitle={`${total} payment records · marked paid only with reference/verification`} icon={Wallet}
        actions={hasPerm("payments:add") && (
          <FormDialog title="Record Payment" description="A payment is marked Paid only when a transaction reference is provided (verified)." fields={fields} onSubmit={create} submitLabel="Record Payment" testId="create-payment-dialog"
            trigger={<Button data-testid="create-payment-btn"><Plus className="h-4 w-4 mr-1.5" /> Record Payment</Button>} />
        )} />
      <DataTable columns={columns} rows={items} loading={loading} rowTestId="payment-table-row" />
      <Pager page={page} limit={25} total={total} onPage={setPage} />
    </div>
  );
}
